import { useState, useEffect, useCallback, useMemo } from "react";

export interface LegendSeries {
  key: string;
  label: string;
  color: string;
}

export function useToggleableSeries(seriesKeys: LegendSeries[]) {
  const keyList = useMemo(() => seriesKeys.map(s => s.key), [seriesKeys]);

  const [visibleSeries, setVisibleSeries] = useState<Set<string>>(
    () => new Set(keyList)
  );

  useEffect(() => {
    setVisibleSeries(new Set(keyList));
  }, [keyList.join(",")]);

  const toggleSeries = useCallback((key: string) => {
    setVisibleSeries(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) {
          next.delete(key);
        }
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const isVisible = useCallback(
    (key: string) => visibleSeries.has(key),
    [visibleSeries]
  );

  return { visibleSeries, toggleSeries, isVisible };
}

interface InteractiveLegendProps {
  series: LegendSeries[];
  visibleSeries: Set<string>;
  onToggle: (key: string) => void;
  compact?: boolean;
  testIdPrefix?: string;
}

export function InteractiveLegend({
  series,
  visibleSeries,
  onToggle,
  compact = false,
  testIdPrefix = "legend",
}: InteractiveLegendProps) {
  return (
    <div
      className={`flex flex-wrap items-center justify-center ${
        compact ? "gap-1 mb-2 min-h-[28px]" : "gap-2 mb-3"
      }`}
      data-testid={`${testIdPrefix}-interactive`}
    >
      {series.map((s) => {
        const active = visibleSeries.has(s.key);
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onToggle(s.key)}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border transition-all cursor-pointer whitespace-nowrap ${
              compact ? "text-xs h-6" : "text-xs"
            } ${
              active
                ? "border-border bg-background"
                : "border-muted bg-muted/30 opacity-50"
            }`}
            data-testid={`${testIdPrefix}-toggle-${s.key}`}
          >
            <span
              className={`inline-block rounded-sm flex-shrink-0 ${
                compact ? "w-2.5 h-2.5" : "w-3 h-3"
              }`}
              style={{
                backgroundColor: active ? s.color : "transparent",
                border: `2px solid ${s.color}`,
              }}
            />
            <span className={active ? "" : "line-through"}>{s.label}</span>
          </button>
        );
      })}
    </div>
  );
}
